import type { StatusMatchCampaignConfig, StatusMatchLabPayload } from './statusMatchTypes'
import { isStatusMatchLabPayload } from './statusMatchTypes'

interface LabFlagEntry {
  enabled?: boolean
  value?: unknown
}

function isLabFlagEntry(value: unknown): value is LabFlagEntry {
  return typeof value === 'object' && value !== null
}

function toCampaignConfig(payload: StatusMatchLabPayload): StatusMatchCampaignConfig {
  return { config: payload }
}

function isCampaignEnabledForRegion(
  payload: StatusMatchLabPayload,
  regionCode: string,
): boolean {
  return payload.allowedRegions.includes(regionCode)
}

/**
 * Resolves status match campaign configs from a Lab feature flag result.
 * Supports legacy single-object payloads and multi-campaign array payloads (same as tier upgrade).
 */
export function resolveStatusMatchCampaignsFromFlag(
  flag: App.LabFeatureFlagResult | undefined,
  regionCode: string,
): Array<StatusMatchCampaignConfig> {
  if (!flag?.enabled) return []

  const { value } = flag

  if (!Array.isArray(value)) {
    if (!isStatusMatchLabPayload(value)) return []
    if (!isCampaignEnabledForRegion(value, regionCode)) return []
    return [toCampaignConfig(value)]
  }

  const campaigns: Array<StatusMatchCampaignConfig> = []
  for (const entry of value) {
    if (!isLabFlagEntry(entry) || entry.enabled === false) continue
    if (!isStatusMatchLabPayload(entry.value)) continue
    if (!isCampaignEnabledForRegion(entry.value, regionCode)) continue
    campaigns.push(toCampaignConfig(entry.value))
  }

  return campaigns
}

export function resolveStatusMatchCampaignFromFlag(
  flag: App.LabFeatureFlagResult | undefined,
  regionCode: string,
  slug: string,
): StatusMatchCampaignConfig | undefined {
  return resolveStatusMatchCampaignsFromFlag(flag, regionCode).find(
    (campaign) => campaign.config.slug === slug,
  )
}
